import React, { useMemo } from 'react'
import styles from './PlayersGrid.module.scss'
import { Drag } from '../Drag/Drag'
import { usePlayerFormModal } from '../../metrics_server/players/modals'
import { Button } from '../Button/Button'
import {
  useFormattedSession,
  usePlayerAssignmentFunctions
} from '../../metrics_server/sessions/hooks'
import { SessionPlayer } from '../Player/SessionPlayer/SessionPlayer'
import { usePlayerFilter } from '../../metrics_server/players/filter'
import Select from '../Forms/Inputs/Select/Select'
import BasicSelect from '../Material/Select'
import { Grid } from '@mui/material'
import { PlayerInstructionsCard } from '../ExpandableInfoCard/ExpandableInfoCard'

export interface PlayersGridProps {
  teamId: string
  sessionId: string | 'setup'
  viewMode?: number
  setViewMode?: (value: number) => void
  selectionManager?: any
}

export const PlayersGrid = ({
  teamId,
  sessionId,
  viewMode = 0,
  setViewMode,
  selectionManager
}: PlayersGridProps) => {
  const { id, sport, isTrainingMode } = useFormattedSession(sessionId)

  const { filteredPlayers, filters, updateFilterValue } = usePlayerFilter(
    {
      team: [teamId]
    },
    'setup'
  )

  const handleSearchChange = (event) => {
    updateFilterValue('searchStr', event)
  }

  const { addAllTeamsPlayersToSession, removeAllTeamsPlayersFromSession } =
    usePlayerAssignmentFunctions(teamId)

  const { openPlayerFormModal } = usePlayerFormModal()

  // Split players into four columns for the list view
  const playerColumns = useMemo(() => {
    const players = filteredPlayers.list
    const columnSize = Math.ceil(players.length / 4)

    return {
      column1: players.slice(0, columnSize),
      column2: players.slice(columnSize, columnSize * 2),
      column3: players.slice(columnSize * 2, columnSize * 3),
      column4: players.slice(columnSize * 3)
    }
  }, [filteredPlayers.list])

  return (
    <div className={styles.playerAssignmentGridContainer}>
      <div className={styles.filtersContainer}>
        {!sport.props.features.positionNumbers && (
          <div className={styles.headerRow}>
            <h5>Add Players to Session</h5>
          </div>
        )}

        <Grid container justifyContent='space-between' alignItems='center'>
          <Grid item xs={2}>
            <BasicSelect
              label={'Search Players'}
              variant='standard'
              selected={filters.searchStr.value}
              onChange={(e) => handleSearchChange(e)}
              labelShrink={true}
              size={'small'}
              fontSize={20}
            />
          </Grid>
          <Grid item xs={2}>
            <Select
              className='small-select'
              options={filters.assigned.options}
              updateValue={updateFilterValue}
              input={{
                name: 'assigned',
                value: filters.assigned.value
              }}
            />
          </Grid>
          <Grid item xs={2}>
            {/* Add new player button */}
            <Button
              handleClick={() => openPlayerFormModal(sport, null, teamId)}
              className={'btn--primary-thin'}
              style={{ alignSelf: 'center' }}
            >
              Create Player
            </Button>
          </Grid>

          {(isTrainingMode || !sport.props.features.positionNumbers) && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ padding: '2px' }}>
                <Button
                  className={'btn--primary-thin'}
                  handleClick={addAllTeamsPlayersToSession}
                  style={{ alignSelf: 'center', width: '100px' }}
                >
                  Add All
                </Button>
              </div>
              <div style={{ padding: '2px' }}>
                <Button
                  className={'btn--border-thin'}
                  handleClick={removeAllTeamsPlayersFromSession}
                  style={{ alignSelf: 'center', width: '100px' }}
                >
                  Remove All
                </Button>
              </div>
            </div>
          )}
        </Grid>
      </div>

      {/* Conditional rendering based on view mode (0 for grid, 1 for list) */}
      {viewMode === 0 ? (
        /* Grid view - keep existing drag and drop functionality */
        <div className={styles.playerListScroller}>
          <div className={styles.playerAssignmentGridContent}>
            {filteredPlayers.list.map((player) => (
              <React.Fragment key={player.id}>
                <Drag>
                  <SessionPlayer sessionId={id} playerId={player.id} />
                </Drag>
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : (
        /* List view with selection functionality */
        <div className={styles.playerListViewContainer}>
          {/* Info text for list mode */}
          <PlayerInstructionsCard />
          <div className={styles.playerListScroller}>
            <div className={styles.playerListColumns}>
              {/* First column */}
              <div className={styles.playerListColumn}>
                {playerColumns.column1.map((player) => (
                  <React.Fragment key={player.id}>
                    <SessionPlayer
                      sessionId={id}
                      playerId={player.id}
                      list={true}
                      selectionManager={selectionManager}
                    />
                  </React.Fragment>
                ))}
              </div>

              {/* Second column */}
              <div className={styles.playerListColumn}>
                {playerColumns.column2.map((player) => (
                  <React.Fragment key={player.id}>
                    <SessionPlayer
                      sessionId={id}
                      playerId={player.id}
                      list={true}
                      selectionManager={selectionManager}
                    />
                  </React.Fragment>
                ))}
              </div>

              {/* Third column */}
              <div className={styles.playerListColumn}>
                {playerColumns.column3.map((player) => (
                  <React.Fragment key={player.id}>
                    <SessionPlayer
                      sessionId={id}
                      playerId={player.id}
                      list={true}
                      selectionManager={selectionManager}
                    />
                  </React.Fragment>
                ))}
              </div>

              {/* Fourth column */}
              <div className={styles.playerListColumn}>
                {playerColumns.column4.map((player) => (
                  <React.Fragment key={player.id}>
                    <SessionPlayer
                      sessionId={id}
                      playerId={player.id}
                      list={true}
                      selectionManager={selectionManager}
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
